import { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { adminMenu, can, ROLE_LABELS } from "@/lib/perms";

export const AdminLayout = () => {
    const { user, refresh } = useAuth();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    useEffect(() => { refresh(); setOpen(false); }, [pathname, refresh]);
    const menu = adminMenu(user);
    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 grid gap-6 lg:grid-cols-[15rem_minmax(0,1fr)] items-start">
            <nav aria-label="Menu amministrazione" className="edi-card p-3 edi-side-sticky" data-testid="admin-sidebar">
                <div className="flex items-center justify-between gap-2">
                    <p className="edi-eyebrow px-2 py-1" data-testid="admin-role-label">{user.role === "admin_sede" ? `Admin di sede · ${user.spin?.name || ""}` : ROLE_LABELS[user.role]}</p>
                    <button type="button" className="btn-ghost lg:hidden" aria-expanded={open} aria-controls="admin-menu-list" onClick={() => setOpen((o) => !o)} data-testid="admin-menu-toggle">
                        {open ? <X aria-hidden="true" size={20} /> : <Menu aria-hidden="true" size={20} />} Sezioni
                    </button>
                </div>
                <ul id="admin-menu-list" className={`${open ? "flex" : "hidden"} lg:flex flex-col gap-1 mt-2`}>
                    {menu.map(([path, label, Icon]) => (
                        <li key={path}>
                            <NavLink end={!path} to={`/amministrazione${path ? `/${path}` : ""}`} data-testid={`admin-menu-${path || "home"}`}
                                className={({ isActive }) => `edi-side-item ${isActive ? "is-active" : ""}`}>
                                {Icon && <Icon aria-hidden="true" size={18} className="shrink-0" />}{label}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </nav>
            <div className="min-w-0"><Outlet /></div>
        </div>
    );
};

const firstAccessible = (user) => adminMenu(user).find(([p]) => p && p !== "account" && p !== "esporta");

export const Perm = ({ k, any, system, level = "V", children }) => {
    const { user } = useAuth();
    const ok = system ? user.role === "system_admin" : (any || [k]).some((x) => can(user, x, level));
    return ok ? children : <AccessDenied />;
};

export const AccessDenied = () => (
    <section data-testid="access-denied">
        <h1 className="text-3xl font-extrabold">Accesso negato</h1>
        <p className="mt-4">Non hai i permessi per visualizzare questa pagina.</p>
        <NavLink to="/amministrazione" end className="btn-primary mt-6" data-testid="access-denied-home-link">Torna alla tua area</NavLink>
    </section>
);

export const AdminHome = ({ children }) => {
    const { user } = useAuth();
    if (can(user, "panoramica")) return children;
    const first = firstAccessible(user);
    if (first) return <Navigate to={`/amministrazione/${first[0]}`} replace />;
    return (
        <section data-testid="admin-no-section">
            <h1 className="text-3xl font-extrabold">Area amministrazione</h1>
            <p className="mt-4">Nessuna sezione disponibile. Contatta l'Amministrazione di sistema</p>
        </section>
    );
};

const SUBTITLES = [
    [/^Panoramica nazionale/, "Indicatori di tutte le aziende registrate"], [/^Panoramica/, "Indicatori delle aziende della tua sede"],
    [/^Confronto territori/, "Confronto per Regione e sede SPIN"], [/^Analisi dettagliata/, "Distribuzione delle risposte con filtri avanzati"],
    [/^Aziende/, "Elenco delle aziende registrate"], [/^Sedi/, "Sportelli SPIN e territori di competenza"],
    [/^Amministratori di sede/, "Account degli amministratori di sede"], [/^Utenti e permessi/, "Account amministratori, ruoli e permessi"],
    [/^Questionario/, "Versioni e domande dell'assessment"], [/^Bozza versione/, "Modifica della bozza del questionario"],
    [/^Campi di registrazione/, "Campi del modulo di registrazione"], [/^Analytics/, "Accessi e utilizzo della piattaforma"],
    [/^Registro accessi/, "Elenco dettagliato degli eventi di accesso"], [/^Contenuti del sito/, "Testi, immagini ed email del sito pubblico"],
    [/^Impostazioni/, "Assistente AI, privacy e piè di pagina"], [/^Il mio account/, "Dati di accesso e password"],
    [/^Esporta/, "Esportazione dei dati in Excel e CSV"],
];

export const PageHead = ({ title, subtitle, children }) => {
    const sub = subtitle ?? (SUBTITLES.find(([re]) => re.test(title || ""))?.[1] || "Dettaglio azienda");
    return (
        <div className="surface-dark rounded-xl px-5 py-5 sm:px-6 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="admin-title-strip">
            <div className="min-w-0">
                <h1 className="text-3xl sm:text-4xl font-extrabold break-words" data-testid="admin-page-title">{title}</h1>
                <p className="mt-1 text-edi-muted" data-testid="admin-page-subtitle">{sub}</p>
            </div>
            {children && <div className="flex flex-wrap items-center gap-3">{children}</div>}
        </div>
    );
};
